import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { apiRequest } from "../../../../shared/api/client";
import { can } from "../../../../shared/auth/access";
import type { SessionUser } from "../../../../shared/auth/access";
import type { Rfq, RfqOffers, UnifiedEvaluation, ContractCompliance, RfqCommunications } from "../../domain/rfq-types";
import type { ProcurementScope } from "../shared/useProcurementScope";
import { rfqUiMessage } from "./rfq-presentation";
import { useRfqReadScope } from "./useRfqReadScope";

type ReadScope = ReturnType<typeof useRfqReadScope>;
type State = { readScope: ReadScope; offers: RfqOffers | null; evaluation: UnifiedEvaluation | null; contractCompliance: ContractCompliance[]; communications: RfqCommunications; governanceError: string };
const empty = (readScope: ReadScope): State => ({ readScope, offers: null, evaluation: null, contractCompliance: [], communications: { questions: [], announcements: [] }, governanceError: "" });
export function useRfqGovernanceData({ scope, rfq, user }: { scope: ProcurementScope; rfq: Rfq | null; user: SessionUser }) {
  const readScope = useRfqReadScope(scope, rfq?.id);
  const [state, setState] = useState(() => empty(readScope));
  const versions = useRef({ load: 0, evaluation: 0, compliance: 0 });
  const inFlight = useRef<{ readScope: ReadScope; promise: Promise<boolean> } | null>(null);
  const setGovernanceError: Dispatch<SetStateAction<string>> = useCallback(value => {
    if (!readScope.isCurrent()) return;
    setState(current => {
      const previous = current.readScope === readScope ? current : empty(readScope);
      return { ...previous, governanceError: typeof value === "function" ? value(previous.governanceError) : value };
    });
  }, [readScope]);
  const setEvaluation: Dispatch<SetStateAction<UnifiedEvaluation | null>> = useCallback(value => {
    if (!readScope.isCurrent()) return;
    versions.current.evaluation++;
    setState(current => {
      const previous = current.readScope === readScope ? current : empty(readScope);
      return { ...previous, evaluation: typeof value === "function" ? value(previous.evaluation) : value };
    });
  }, [readScope]);
  const setContractCompliance: Dispatch<SetStateAction<ContractCompliance[]>> = useCallback(value => {
    if (!readScope.isCurrent()) return;
    versions.current.compliance++;
    setState(current => {
      const previous = current.readScope === readScope ? current : empty(readScope);
      return { ...previous, contractCompliance: typeof value === "function" ? value(previous.contractCompliance) : value };
    });
  }, [readScope]);
  const questionAccess = can(user, "supplier_portal.question.manage");
  const loadGovernance = useCallback((rfqId: string, afterWrite = false): Promise<boolean> => {
    if (!readScope.isCurrent() || rfqId !== rfq?.id) return Promise.resolve(false);
    if (!afterWrite && inFlight.current?.readScope === readScope) return inFlight.current.promise;
    const read = async () => {
      const snapshot = { ...versions.current, load: ++versions.current.load };
      const isCurrent = () => readScope.isCurrent() && snapshot.load === versions.current.load;
      setGovernanceError("");
      try {
        const offers = await apiRequest<RfqOffers>(`/api/rfqs/${rfqId}/offers`);
        if (!isCurrent()) return false;
        setState(current => ({ ...(current.readScope === readScope ? current : empty(readScope)), offers }));
        const evaluation = await apiRequest<UnifiedEvaluation>(`/api/rfqs/${rfqId}/evaluations/latest`).catch(() => null);
        if (!isCurrent()) return false;
        if (snapshot.evaluation === versions.current.evaluation) {
          setState(current => ({ ...(current.readScope === readScope ? current : empty(readScope)), evaluation }));
        }
        const contractCompliance = await apiRequest<ContractCompliance[]>(`/api/contract-compliance/${rfqId}`).catch(() => []);
        if (!isCurrent()) return false;
        if (snapshot.compliance === versions.current.compliance) {
          setState(current => ({ ...(current.readScope === readScope ? current : empty(readScope)), contractCompliance }));
        }
        const communications = questionAccess
          ? await apiRequest<RfqCommunications>(`/api/rfqs/${rfqId}/communications`)
          : { questions: [], announcements: [] };
        if (!isCurrent()) return false;
        setState(current => ({ ...(current.readScope === readScope ? current : empty(readScope)), communications }));
        return true;
      } catch (value) {
        if (isCurrent()) setGovernanceError(rfqUiMessage(value, "Teklif güvenliği bilgileri yüklenemedi."));
        return false;
      }
    };
    const operation = { readScope, promise: read() };
    inFlight.current = operation;
    const clear = () => { if (inFlight.current === operation) inFlight.current = null; };
    void operation.promise.then(clear, clear);
    return operation.promise;
  }, [readScope, rfq?.id, questionAccess, setGovernanceError]);
  useEffect(() => {
    if (rfq?.id && rfq.status !== "DRAFT") void loadGovernance(rfq.id);
  }, [rfq?.id, rfq?.status, loadGovernance]);
  return { ...(state.readScope === readScope ? state : empty(readScope)), setEvaluation, setContractCompliance, setGovernanceError, loadGovernance };
}
